import React from "react";

/* ── Inline markdown: **bold** and *italic* only ───────────────────────────── */
function parseInline(text, lineKey) {
  const parts = [];
  // Match **bold** before *italic* to avoid partial overlap
  const regex = /\*\*([^*]+?)\*\*|\*([^*]+?)\*/g;
  let last = 0;
  let idx = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      parts.push(<strong key={`${lineKey}-b${idx++}`}>{m[1]}</strong>);
    } else {
      parts.push(<em key={`${lineKey}-i${idx++}`}>{m[2]}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
}

/* ── Line parser ────────────────────────────────────────────────────────────── */
export function parseContentLines(content) {
  if (!content?.trim()) return [];
  return content.split("\n").map((raw, i) => {
    let m;
    m = raw.match(/^-\s\[ \]\s(.+)/);
    if (m) return { type: "task", checked: false, text: m[1], key: i };
    m = raw.match(/^-\s\[x\]\s(.+)/i);
    if (m) return { type: "task", checked: true,  text: m[1], key: i };
    m = raw.match(/^-\s(.+)/);
    if (m) return { type: "bullet", text: m[1], key: i };
    if (!raw.trim()) return { type: "empty", text: "", key: i };
    return { type: "text", text: raw, key: i };
  });
}

/* ── Checkbox icons ─────────────────────────────────────────────────────────── */
const CB_BASE =
  "h-3.5 w-3.5 rounded-[3px] border shrink-0 mt-[1px] inline-flex items-center justify-center";

function CheckboxDone() {
  return (
    <span className={`${CB_BASE} bg-emerald-500 border-emerald-500`}>
      <svg width="8" height="6" viewBox="0 0 8 6" fill="none" aria-hidden="true">
        <path
          d="M1 3l2 2 4-4"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function CheckboxEmpty() {
  return <span className={`${CB_BASE} border-slate-300 dark:border-slate-500`} />;
}

/* ── NoteContentPreview ─────────────────────────────────────────────────────── */
export function NoteContentPreview({ content, maxLines = 3 }) {
  if (!content?.trim()) return null;
  const lines = parseContentLines(content);
  const visible = lines.filter((l) => l.type !== "empty").slice(0, maxLines);
  if (!visible.length) return null;

  return (
    <div className="space-y-0.5 mt-1">
      {visible.map((line) => {
        if (line.type === "task") {
          return (
            <div
              key={line.key}
              className={`flex items-start gap-1.5 text-[13px] leading-snug ${
                line.checked ? "opacity-50" : ""
              }`}
            >
              {line.checked ? <CheckboxDone /> : <CheckboxEmpty />}
              <span
                className={`text-slate-500 dark:text-slate-400 ${
                  line.checked ? "line-through" : ""
                }`}
              >
                {parseInline(line.text, line.key)}
              </span>
            </div>
          );
        }

        if (line.type === "bullet") {
          return (
            <div key={line.key} className="flex items-start gap-1.5 text-[13px] leading-snug">
              <span className="mt-[5px] h-1 w-1 rounded-full bg-slate-400 dark:bg-slate-500 shrink-0" />
              <span className="text-slate-500 dark:text-slate-400">
                {parseInline(line.text, line.key)}
              </span>
            </div>
          );
        }

        return (
          <p
            key={line.key}
            className="text-[13px] text-slate-500 dark:text-slate-400 leading-snug truncate"
          >
            {parseInline(line.text, line.key)}
          </p>
        );
      })}
    </div>
  );
}
